import { Categoria } from '../../servicos/categorias/typeCategoria';
import { Transacao } from '../../servicos/transacoes/typeTransacao';

/**
 * Matemática da tela de Metas (orçamento mensal por categoria) — extraída do
 * componente sem mudança de comportamento, para ser testável com Jest.
 */

/** Converte valores vindos da API (que podem chegar como string) em número seguro. */
export const numero = (valor: unknown): number => {
  const convertido = parseFloat(String(valor ?? 0));
  return Number.isNaN(convertido) ? 0 : convertido;
};

/** Soma o valor das transações de um tipo, agrupado por categoria. */
export const somarPorCategoria = (transacoes: Transacao[], tipo: 'D' | 'R'): Map<number, number> => {
  const mapa = new Map<number, number>();
  transacoes
    .filter((transacao) => transacao.tipo === tipo)
    .forEach((transacao) => {
      if (transacao.categoriaId === null || transacao.categoriaId === undefined) return;
      mapa.set(transacao.categoriaId, (mapa.get(transacao.categoriaId) ?? 0) + numero(transacao.valor));
    });
  return mapa;
};

/** Realizado de uma categoria num mapa de somas (0 quando não há lançamentos). */
export const realizadoDe = (categoria: Categoria, mapa: Map<number, number>): number =>
  categoria.id !== null && categoria.id !== undefined ? mapa.get(categoria.id) ?? 0 : 0;

/**
 * Sugestão de meta: média mensal dos últimos 3 meses (divisor FIXO em 3,
 * mesmo com menos meses de histórico), arredondada pra cima em múltiplos de
 * R$10. Sem histórico retorna 0 — a UI trata 0 como "sem sugestão".
 */
export const sugestaoDeMeta = (categoria: Categoria, gastoHistorico: Map<number, number>): number => {
  const total = realizadoDe(categoria, gastoHistorico);
  if (total <= 0) return 0;
  return Math.ceil(total / 3 / 10) * 10;
};

export type ResumoDoOrcamento = {
  orcamentoTotal: number;
  gastoOrcado: number;
  disponivel: number;
  pctOrcamento: number;
  ehMesAtual: boolean;
  ehMesFuturo: boolean;
  /** Mês corrente: projeção run-rate; mês passado: total realizado. */
  projecao: number;
};

/**
 * Visão geral do orçamento: total orçado (soma das metas de despesa), gasto
 * do mês NAS categorias orçadas, disponível e ritmo (projeção de fim de mês
 * no mês corrente; total realizado em mês passado).
 */
export const resumoDoOrcamento = (
  despesasComMeta: Categoria[],
  gastoMes: Map<number, number>,
  mes: Date,
  hoje: Date
): ResumoDoOrcamento => {
  const orcamentoTotal = despesasComMeta.reduce((acc, categoria) => acc + numero(categoria.valorMeta), 0);
  const gastoOrcado = despesasComMeta.reduce((acc, categoria) => acc + realizadoDe(categoria, gastoMes), 0);
  const disponivel = orcamentoTotal - gastoOrcado;
  const pctOrcamento = orcamentoTotal > 0 ? Math.round((gastoOrcado / orcamentoTotal) * 100) : 0;

  const ehMesAtual = mes.getFullYear() === hoje.getFullYear() && mes.getMonth() === hoje.getMonth();
  const ehMesFuturo = mes.getTime() > new Date(hoje.getFullYear(), hoje.getMonth(), 1).getTime();
  const diasDoMes = new Date(mes.getFullYear(), mes.getMonth() + 1, 0).getDate();
  const diasDecorridos = Math.max(hoje.getDate(), 1);
  const projecao = ehMesAtual ? (gastoOrcado / diasDecorridos) * diasDoMes : gastoOrcado;

  return { orcamentoTotal, gastoOrcado, disponivel, pctOrcamento, ehMesAtual, ehMesFuturo, projecao };
};

export type ProgressoDaCategoria = {
  meta: number;
  realizado: number;
  percentual: number;
  /** Positivo: ainda resta; negativo: excedente acima da meta. */
  diferenca: number;
};

/** Progresso de uma categoria contra a meta (gasto, %, restante/excedente). */
export const progressoDaCategoria = (categoria: Categoria, mapa: Map<number, number>): ProgressoDaCategoria => {
  const meta = numero(categoria.valorMeta);
  const realizado = realizadoDe(categoria, mapa);
  const percentual = meta > 0 ? (realizado / meta) * 100 : 0;
  const diferenca = meta - realizado;
  return { meta, realizado, percentual, diferenca };
};
